import { z } from "zod";

const auditEntrySchema = z.object({
	id: z.string(),
	tenantId: z.string(),
	tenantName: z.string().nullable(),
	event: z.string(),
	actor: z.string(),
	tool: z.string().nullable(),
	outcome: z.string(),
	at: z.string(),
});

const overviewSchema = z.object({
	operatorEmail: z.string(),
	counts: z.object({
		tenants: z.number(),
		liveConnections: z.number(),
		pendingApprovals: z.number(),
		enabledAgents: z.number(),
	}),
	recentAudit: z.array(auditEntrySchema),
});

const tenantListSchema = z.object({
	tenants: z.array(
		z.object({
			id: z.string(),
			name: z.string(),
			domain: z.string().nullable(),
			clerkOrgId: z.string().nullable(),
			plan: z.string(),
			status: z.string(),
			createdAt: z.string(),
			connectionCount: z.number(),
			enabledAgentCount: z.number(),
			memberCount: z.number(),
		}),
	),
});

const tenantDetailSchema = z.object({
	tenant: z.object({
		id: z.string(),
		name: z.string(),
		domain: z.string().nullable(),
		clerkOrgId: z.string().nullable(),
		plan: z.string(),
		status: z.string(),
		createdAt: z.string(),
	}),
	pendingApprovals: z.number(),
	connections: z.array(
		z.object({
			provider: z.string(),
			health: z.string(),
			externalAccountId: z.string().nullable(),
			connectedAt: z.string(),
			live: z.boolean(),
		}),
	),
	agents: z.array(
		z.object({
			agentType: z.string(),
			enabled: z.boolean(),
		}),
	),
	members: z.array(
		z.object({
			email: z.string(),
			roles: z.array(z.string()),
			createdAt: z.string(),
		}),
	),
	viewedBy: z.string(),
});

const auditListSchema = z.object({
	entries: z.array(
		auditEntrySchema.extend({
			tier: z.string().nullable().optional(),
			detail: z.record(z.string(), z.unknown()).nullable().optional(),
		}),
	),
});

const staffingSchema = z.object({
	assignments: z.array(
		z.object({
			tenantId: z.string(),
			tenantName: z.string(),
			agentType: z.string(),
			enabled: z.boolean(),
		}),
	),
});

export type AdminOverview = z.infer<typeof overviewSchema>;
export type AdminTenant = z.infer<typeof tenantListSchema>["tenants"][number];
export type AdminTenantDetail = z.infer<typeof tenantDetailSchema>;
export type AdminAuditEntry = z.infer<typeof auditListSchema>["entries"][number];
export type AdminStaffing = z.infer<typeof staffingSchema>;

async function adminGet<T>(
	path: string,
	schema: z.ZodType<T>,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
	try {
		const response = await fetch(path, { credentials: "include" });
		const json: unknown = await response.json().catch(() => null);
		if (!response.ok) {
			const err =
				json &&
				typeof json === "object" &&
				"error" in json &&
				typeof (json as { error: unknown }).error === "string"
					? (json as { error: string }).error
					: `http_${response.status}`;
			return { ok: false, error: err };
		}
		const parsed = schema.safeParse(json);
		if (!parsed.success) {
			return { ok: false, error: "invalid_response" };
		}
		return { ok: true, data: parsed.data };
	} catch {
		return { ok: false, error: "network_error" };
	}
}

export function fetchAdminOverview() {
	return adminGet("/api/admin/overview", overviewSchema);
}

export function fetchAdminTenants() {
	return adminGet("/api/admin/tenants", tenantListSchema);
}

export function fetchAdminTenant(tenantId: string) {
	return adminGet(
		`/api/admin/tenants/${encodeURIComponent(tenantId)}`,
		tenantDetailSchema,
	);
}

export function fetchAdminAudit(limit = 50) {
	return adminGet(`/api/admin/audit?limit=${limit}`, auditListSchema);
}

export function fetchAdminStaffing() {
	return adminGet("/api/admin/staffing", staffingSchema);
}
